import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { extractNumericId } from '../../lib/userIdExtractor';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface PendingPayment {
  id: string;
  amount: number;
  shares_to_purchase: number;
  network: string;
  transaction_hash: string | null;
  screenshot_url: string | null;
  created_at: string;
  status: string;
}

interface PendingPaymentsListProps {
  userId: number;
  onResume: (payment: PendingPayment) => void;
  onRefresh?: () => void;
}

export const PendingPaymentsList: React.FC<PendingPaymentsListProps> = ({
  userId,
  onResume
}) => {
  const [payments, setPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPendingPayments();
  }, [userId]);

  const loadPendingPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate userId is numeric before querying
      const validUserId = extractNumericId(userId);
      if (!validUserId) {
        console.error('❌ Invalid userId for PendingPaymentsList:', userId);
        setError('Invalid user ID. Please refresh the page or log out and log back in.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId: validUserId, dataTypes: ['pending_payments'] })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)
      // Defensive: the dashboard-data endpoint returns HTTP 200 but sets
      // result.data[dataType] = { error: '...' } (a NON-array) when a per-dataType
      // query fails. `|| []` does not catch a truthy non-array, so guard with
      // Array.isArray to keep payments an array and never crash payments.map().
      const pending = result.data?.pending_payments;
      if (pending && !Array.isArray(pending)) {
        throw new Error((pending as any).error || 'Failed to load pending payments');
      }
      setPayments(Array.isArray(pending) ? pending : []);
    } catch (err: any) {
      console.error('Error loading pending payments:', err);
      setError(err.message || 'Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTimeElapsed = (dateStr: string): string => {
    const now = Date.now();
    const created = new Date(dateStr).getTime();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const handleResume = (payment: PendingPayment) => {
    if (window.confirm(`You are about to resume a pending payment of $${payment.amount.toFixed(2)} for ${payment.shares_to_purchase} shares. Continue?`)) {
      onResume(payment);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', border: '2px solid #fbbf24', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ color: '#fbbf24' }}>Loading pending payments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>
        <button onClick={loadPendingPayments} style={{ marginTop: '8px', padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return null; // Don't show section if no pending payments
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ color: '#fbbf24', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>⏳</span> Pending Payments ({payments.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {payments.map(payment => (
          <div key={payment.id} style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <div style={{ color: '#fbbf24', fontSize: '18px', fontWeight: 'bold' }}>${payment.amount.toFixed(2)}</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>{payment.shares_to_purchase} shares • {payment.network}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#ffffff', fontSize: '13px' }}>{formatDate(payment.created_at)}</div>
                <div style={{ color: '#fbbf24', fontSize: '13px' }}>{getTimeElapsed(payment.created_at)}</div>
              </div>
            </div>
            <div style={{ marginBottom: '12px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!payment.transaction_hash && <span style={{ color: '#fbbf24' }}>⚠️</span>}
                <span style={{ color: '#ffffff', fontSize: '13px' }}>
                  Transaction Hash: {payment.transaction_hash ? <span style={{ color: '#ffffff', fontFamily: 'monospace', fontSize: '13px' }}>{payment.transaction_hash.slice(0, 20)}...</span> : <span style={{ color: '#fbbf24' }}>Not provided</span>}
                </span>
              </div>
              <div style={{ color: '#ffffff', fontSize: '13px', marginTop: '4px' }}>
                Proof of Payment: {payment.screenshot_url ? <span style={{ color: '#22c55e' }}>✓ Uploaded</span> : <span style={{ color: '#fbbf24' }}>Not uploaded</span>}
              </div>
            </div>
            <button onClick={() => handleResume(payment)} style={{ width: '100%', padding: '10px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>
              Resume Payment
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingPaymentsList;

